/// Low-precision planetary positions — Venus, Mars, Jupiter, Saturn,
/// the four planets the Nautical Almanac tabulates. Built from Paul
/// Schlyter's Keplerian-elements model: each planet has six orbital
/// elements with linear drifts from J2000.0, plus a Kepler's-equation
/// solve. Accurate to roughly 1–2 arc-minutes for the inner planets
/// and 2–5′ for Jupiter / Saturn over the next decade — adequate for
/// teaching sights; for real precision the current Almanac is the
/// authority.
///
/// All angles are degrees unless explicitly marked.

import { DEG, RAD, daysSinceJ2000, normalize360 } from "./time";
import { sunPosition } from "./sun";

export type Planet = "venus" | "mars" | "jupiter" | "saturn";

export interface PlanetPosition {
  /// Declination, degrees (N positive, S negative).
  decDeg: number;
  /// Greenwich Hour Angle, degrees [0, 360).
  ghaDeg: number;
  /// Right ascension, decimal hours [0, 24).
  raHours: number;
  /// Geocentric distance, astronomical units.
  distanceAu: number;
  /// Apparent magnitude. Rough first-order estimate — actual values
  /// in the Almanac for sight planning, this is a guide for visibility.
  magnitude: number;
}

interface OrbitalElements {
  /// Longitude of the ascending node.
  N: (d: number) => number;
  /// Inclination to the ecliptic.
  i: (d: number) => number;
  /// Argument of perihelion.
  w: (d: number) => number;
  /// Semi-major axis, AU.
  a: number;
  /// Eccentricity.
  e: (d: number) => number;
  /// Mean anomaly.
  M: (d: number) => number;
}

// Schlyter's J2000 elements, with rates per day. Sun included so we
// can compute Earth's heliocentric position (= − Sun's geocentric).
const ELEMENTS: Record<Planet | "sun", OrbitalElements> = {
  sun: {
    N: () => 0,
    i: () => 0,
    w: (d) => 282.9404 + 4.70935e-5 * d,
    a: 1.0,
    e: (d) => 0.016709 - 1.151e-9 * d,
    M: (d) => 356.0470 + 0.9856002585 * d,
  },
  venus: {
    N: (d) => 76.6799 + 2.46590e-5 * d,
    i: (d) => 3.3946 + 2.75e-8 * d,
    w: (d) => 54.8910 + 1.38374e-5 * d,
    a: 0.723330,
    e: (d) => 0.006773 - 1.302e-9 * d,
    M: (d) => 48.0052 + 1.6021302244 * d,
  },
  mars: {
    N: (d) => 49.5574 + 2.11081e-5 * d,
    i: (d) => 1.8497 - 1.78e-8 * d,
    w: (d) => 286.5016 + 2.92961e-5 * d,
    a: 1.523688,
    e: (d) => 0.093405 + 2.516e-9 * d,
    M: (d) => 18.6021 + 0.5240207766 * d,
  },
  jupiter: {
    N: (d) => 100.4542 + 2.76854e-5 * d,
    i: (d) => 1.3030 - 1.557e-7 * d,
    w: (d) => 273.8777 + 1.64505e-5 * d,
    a: 5.20256,
    e: (d) => 0.048498 + 4.469e-9 * d,
    M: (d) => 19.8950 + 0.0830853001 * d,
  },
  saturn: {
    N: (d) => 113.6634 + 2.38980e-5 * d,
    i: (d) => 2.4886 - 1.081e-7 * d,
    w: (d) => 339.3939 + 2.97661e-5 * d,
    a: 9.55475,
    e: (d) => 0.055546 - 9.499e-9 * d,
    M: (d) => 49.9444 + 0.0334442282 * d,
  },
};

/// Solve Kepler's equation `E − e·sin(E) = M` for the eccentric
/// anomaly E. M, E in radians. Newton–Raphson converges in 3–4 steps
/// for e < 0.1 (all the bodies here); we run 6 for extra safety.
function solveKepler(M: number, e: number): number {
  let E = M + e * Math.sin(M) * (1 + e * Math.cos(M));
  for (let k = 0; k < 6; k++) {
    const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < 1e-9) break;
  }
  return E;
}

/// Heliocentric ecliptic position {x, y, z} (AU) for a body whose
/// orbital elements are known. The "elements" param is the body's
/// `OrbitalElements`, evaluated at days-since-J2000 `d`.
function heliocentric(el: OrbitalElements, d: number): { x: number; y: number; z: number } {
  const M = el.M(d) * DEG;
  const e = el.e(d);
  const E = solveKepler(M, e);
  const xv = el.a * (Math.cos(E) - e);
  const yv = el.a * Math.sqrt(1 - e * e) * Math.sin(E);
  const r = Math.sqrt(xv * xv + yv * yv);
  const v = Math.atan2(yv, xv);
  const N = el.N(d) * DEG;
  const i = el.i(d) * DEG;
  const w = el.w(d) * DEG;
  const vw = v + w;
  const x = r * (Math.cos(N) * Math.cos(vw) - Math.sin(N) * Math.sin(vw) * Math.cos(i));
  const y = r * (Math.sin(N) * Math.cos(vw) + Math.cos(N) * Math.sin(vw) * Math.cos(i));
  const z = r * Math.sin(vw) * Math.sin(i);
  return { x, y, z };
}

/// Apparent magnitude at the body's current geocentric distance r and
/// heliocentric distance R. Schlyter's coarse formulae — useful for
/// "is it bright enough at twilight to find?" rather than photometry.
function apparentMagnitude(
  planet: Planet,
  R: number,
  r: number,
  phaseDeg: number,
): number {
  const base = 5 * Math.log10(R * r);
  switch (planet) {
    case "venus":
      return -4.34 + base + 0.013 * phaseDeg + 4.2e-7 * phaseDeg ** 3;
    case "mars":
      return -1.51 + base + 0.016 * phaseDeg;
    case "jupiter":
      return -9.25 + base + 0.005 * phaseDeg;
    case "saturn":
      // Schlyter's includes ring tilt; we leave that out — typical
      // visual mag varies 0–1.5 around -0.5 to +1.5 depending on phase
      // and rings. Coarse estimate.
      return -9.0 + base + 0.044 * phaseDeg;
  }
}

export function planetPosition(planet: Planet, date: Date): PlanetPosition {
  const d = daysSinceJ2000(date);

  // Body's heliocentric ecliptic position.
  const p = heliocentric(ELEMENTS[planet], d);

  // Earth's heliocentric position = − Sun's geocentric. The Sun's
  // ecliptic coordinates come from its own Keplerian element set,
  // treated as if "Sun orbits Earth" in geocentric coordinates.
  const sunEl = ELEMENTS.sun;
  const Ms = sunEl.M(d) * DEG;
  const es = sunEl.e(d);
  const Es = solveKepler(Ms, es);
  const xvs = Math.cos(Es) - es;
  const yvs = Math.sqrt(1 - es * es) * Math.sin(Es);
  const rs = Math.sqrt(xvs * xvs + yvs * yvs);
  const vs = Math.atan2(yvs, xvs);
  const lonSun = vs + sunEl.w(d) * DEG;
  // Sun's geocentric ecliptic = (rs·cos, rs·sin, 0); Earth's
  // heliocentric is the negative of that.
  const earthHelio = {
    x: -rs * Math.cos(lonSun),
    y: -rs * Math.sin(lonSun),
    z: 0,
  };

  // Geocentric ecliptic position of the planet.
  const xg = p.x - earthHelio.x;
  const yg = p.y - earthHelio.y;
  const zg = p.z - earthHelio.z;
  const distanceAu = Math.sqrt(xg * xg + yg * yg + zg * zg);

  // Ecliptic → equatorial.
  const epsilon = (23.4393 - 0.0000004 * d) * DEG;
  const xe = xg;
  const ye = yg * Math.cos(epsilon) - zg * Math.sin(epsilon);
  const ze = yg * Math.sin(epsilon) + zg * Math.cos(epsilon);

  const raRad = Math.atan2(ye, xe);
  const decRad = Math.atan2(ze, Math.sqrt(xe * xe + ye * ye));
  const raDeg = (raRad * RAD + 360) % 360;
  const decDeg = decRad * RAD;
  const raHours = raDeg / 15;

  // GHA via GMST (same form as the Sun + Moon ephemerides).
  const gmstHours = ((18.697374558 + 24.06570982441908 * d) % 24 + 24) % 24;
  const ghaDeg = normalize360(gmstHours * 15 - raDeg);

  // Phase angle (Sun–planet–Earth) for magnitude.
  const R = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
  const cosPhase = (R * R + distanceAu * distanceAu - rs * rs) / (2 * R * distanceAu);
  const phaseDeg = Math.acos(Math.max(-1, Math.min(1, cosPhase))) * RAD;
  const magnitude = apparentMagnitude(planet, R, distanceAu, phaseDeg);

  return { decDeg, ghaDeg, raHours, distanceAu, magnitude };
}

export const NAV_PLANETS: readonly Planet[] = ["venus", "mars", "jupiter", "saturn"];

export type PlanetVisibility =
  | "Morning star"
  | "Evening star"
  | "Lost in the Sun's glare";

export interface PlanetSighting {
  /// Elongation east of the Sun, degrees [−180, +180]. Positive =
  /// east of Sun (sets after Sun → "evening star"); negative = west
  /// of Sun (rises before Sun → "morning star").
  elongationDeg: number;
  /// How the planet appears to a twilight navigator.
  visibility: PlanetVisibility;
  /// Difference between planet's and Sun's RA, decimal hours. A
  /// positive value of e.g. 3 h means the planet transits the
  /// observer's meridian about three hours after the Sun.
  meridianOffsetHours: number;
  /// One-line narrative used in the Reference visibility panel.
  note: string;
}

/// Visibility tag for a planet at the given date. The threshold for
/// "lost in the glare" is ~10° elongation — at less than that, the
/// planet sets within ~40 min of sunset (or rises within 40 min of
/// sunrise) and is rarely usable for a twilight sight.
export function planetVisibility(planet: Planet, date: Date): PlanetSighting {
  const sun = sunPosition(date);
  const p = planetPosition(planet, date);

  // Signed elongation, −180..+180°. Positive east of Sun.
  let elong = (p.raHours - sun.raHours) * 15;
  elong = ((elong + 540) % 360) - 180;

  let visibility: PlanetVisibility;
  if (Math.abs(elong) < 10) visibility = "Lost in the Sun's glare";
  else if (elong > 0) visibility = "Evening star";
  else visibility = "Morning star";

  const meridianOffsetHours = elong / 15;
  const absHours = Math.abs(meridianOffsetHours);
  const wholeMin = Math.round(absHours * 60);
  const h = Math.floor(wholeMin / 60);
  const mm = wholeMin % 60;
  const hms = h > 0 ? `${h}h ${mm.toString().padStart(2, "0")}m` : `${mm}m`;

  let note: string;
  if (visibility === "Lost in the Sun's glare") {
    note = `Within ${Math.abs(elong).toFixed(1)}° of the Sun — too close for a useful twilight sight.`;
  } else if (visibility === "Evening star") {
    note = `Sets about ${hms} after the Sun; visible at evening twilight in the western sky.`;
  } else {
    note = `Rises about ${hms} before the Sun; visible at morning twilight in the eastern sky.`;
  }

  // Planet-specific extra notes — the kind the RYA "planet notes"
  // section adds beside the daily ephemeris.
  const extra = PLANET_GENERAL_NOTE[planet];
  if (extra) note += " " + extra;

  return { elongationDeg: elong, visibility, meridianOffsetHours, note };
}

const PLANET_GENERAL_NOTE: Record<Planet, string> = {
  venus:
    "Never more than ~47° from the Sun; the brightest planet by a wide margin (mag −4) and the easiest twilight sight.",
  mars:
    "Brightens dramatically near opposition (every 2.1 years) and fades to mag +1.5 near conjunction; reddish tint.",
  jupiter:
    "Bright (mag −2) all year except for ~3 weeks around conjunction; the most consistent twilight planet after Venus.",
  saturn:
    "Moderate brightness (mag +0.5 around opposition, +1.5 near conjunction); slowest mover of the four — same constellation for months.",
};
